(() => {
  const sectionSelector = '[data-hov-collector-stories]';
  const enhancedSections = new WeakSet();
  const observedSections = new WeakSet();

  const isReviewPhoto = (image) => {
    const source = `${image.currentSrc || image.src || image.dataset.src || ''} ${image.className}`;
    const context = image.closest('a, [class*="picture"], [class*="media"]');
    const contextValue = context ? `${context.className} ${context.getAttribute('href') || ''}` : '';

    return /judge\.me\/reviews|cdn\.judge\.me|review[_-](picture|image|media)|rev__pic/i.test(`${source} ${contextValue}`);
  };

  const buildPhotoRow = (section) => {
    const photoRegion = section.querySelector('[data-hov-review-photos]');
    const photoTrack = section.querySelector('[data-hov-review-photo-track]');
    if (!photoRegion || !photoTrack) return;

    const sources = [...section.querySelectorAll('.jdgm-carousel-item img')]
      .filter(isReviewPhoto)
      .filter((image, index, images) => {
        const source = image.currentSrc || image.src || image.dataset.src;
        return source && images.findIndex((item) => (item.currentSrc || item.src || item.dataset.src) === source) === index;
      });

    photoTrack.replaceChildren();
    if (!sources.length) {
      photoRegion.hidden = true;
      return;
    }

    const visibleLimit = window.matchMedia('(max-width: 749px)').matches ? 4 : 6;
    const visibleSources = sources.slice(0, visibleLimit);

    visibleSources.forEach((image, index) => {
      const originalLink = image.closest('a');
      const control = originalLink ? originalLink.cloneNode(false) : document.createElement('button');
      const clonedImage = image.cloneNode(true);

      control.classList.add('hov-collector-photo');
      control.setAttribute('role', 'listitem');
      control.setAttribute('aria-label', `Open customer photo ${index + 1} of ${sources.length}`);
      if (control.tagName === 'BUTTON') control.type = 'button';

      clonedImage.removeAttribute('width');
      clonedImage.removeAttribute('height');
      clonedImage.loading = 'lazy';
      clonedImage.decoding = 'async';
      control.append(clonedImage);

      if (index === visibleSources.length - 1 && sources.length > visibleSources.length) {
        const more = document.createElement('span');
        more.className = 'hov-collector-photo__more';
        more.textContent = `+${sources.length - visibleSources.length}`;
        more.setAttribute('aria-hidden', 'true');
        control.append(more);
      }

      photoTrack.append(control);
    });

    photoRegion.hidden = false;
  };

  const enhanceCards = (section) => {
    const cards = [...section.querySelectorAll('.jdgm-carousel-item')];
    if (!cards.length) return false;

    cards.forEach((card) => {
      card.setAttribute('role', 'listitem');
      const reviewerName = card.querySelector('.jdgm-carousel-item__reviewer-name');
      if (reviewerName && /^(buyer\s*\d*|buy\s*!?)$/i.test(reviewerName.textContent.trim())) {
        reviewerName.textContent = 'Collector';
        reviewerName.dataset.hovNormalizedName = 'true';
      }

      const rating = card.querySelector('.jdgm-carousel-item__review-rating');
      if (rating && !rating.getAttribute('aria-label')) rating.setAttribute('aria-label', '5 stars');

      const body = card.querySelector('.jdgm-carousel-item__review-body');
      if (body && body.textContent.trim().length > 180 && !card.querySelector('.hov-collector-read-more')) {
        body.classList.add('hov-collector-review-copy--clamped');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hov-collector-read-more';
        button.textContent = 'Read more';
        button.setAttribute('aria-expanded', 'false');
        body.after(button);
      }

      card.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
      });
    });

    const wrapper = section.querySelector('.jdgm-carousel__item-wrapper');
    if (wrapper) wrapper.setAttribute('role', 'list');
    buildPhotoRow(section);
    section.classList.add('hov-collector-stories--ready');
    enhancedSections.add(section);
    return true;
  };

  const initializeSection = (section) => {
    enhanceCards(section);
    if (window.location.hash.includes('collector_reviews')) {
      window.setTimeout(() => section.scrollIntoView({ block: 'start' }), 500);
    }
    if (observedSections.has(section)) return;

    const carousel = section.querySelector('.jdgm-carousel-wrapper');
    if (carousel) {
      const observer = new MutationObserver(() => enhanceCards(section));
      observer.observe(carousel, { childList: true, subtree: true });
      observedSections.add(section);
    }

    section.addEventListener('click', (event) => {
      const readMore = event.target.closest('.hov-collector-read-more');
      if (!readMore) return;

      const copy = readMore.previousElementSibling;
      const expanded = readMore.getAttribute('aria-expanded') === 'true';
      copy?.classList.toggle('hov-collector-review-copy--clamped', expanded);
      readMore.setAttribute('aria-expanded', String(!expanded));
      readMore.textContent = expanded ? 'Read more' : 'Show less';
    });
  };

  const openRequestedReviewForm = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('write_review') !== '1') return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      const widget = document.querySelector('#judgeme_product_reviews');
      const trigger = widget?.querySelector(
        '.jdgm-write-rev-link, [data-testid="write-review-button"], [class*="write-review"]'
      );
      attempts += 1;

      if (trigger) {
        window.clearInterval(timer);
        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => trigger.click(), 350);
        url.searchParams.delete('write_review');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      } else if (attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 250);
  };

  const refineProductReviewWidget = (root = document) => {
    const widget = root.querySelector?.('#judgeme_product_reviews') || document.querySelector('#judgeme_product_reviews');
    if (!widget) return;

    const section = widget.closest('.hov-judgeme-reviews');
    const emptyState = section?.querySelector('[data-hov-review-empty]');
    const writeButton = section?.querySelector('[data-hov-review-write]');
    if (!section || !emptyState || !writeButton) return;

    const updateReviewState = () => {
      if (widget.dataset.hovReviewState === 'form') return;
      const trigger = widget.querySelector(
        '.jdgm-write-rev-link, [data-testid="write-review-button"], [class*="write-review"]'
      );
      const hasReviews = widget.querySelector('.jdgm-rev, [data-review-id]');
      if (!trigger && !hasReviews) return;

      emptyState.hidden = Boolean(hasReviews);
      widget.dataset.hovReviewState = hasReviews ? 'reviews' : 'empty';
    };

    updateReviewState();
    if (widget.dataset.hovEmptyStateObserved === 'true') return;

    writeButton.addEventListener('click', () => {
      const trigger = widget.querySelector(
        '.jdgm-write-rev-link, [data-testid="write-review-button"], [class*="write-review"]'
      );
      if (!trigger) return;

      emptyState.hidden = true;
      widget.dataset.hovReviewState = 'form';
      trigger.click();
      window.setTimeout(() => widget.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    });

    const observer = new MutationObserver(updateReviewState);
    observer.observe(widget, { childList: true, subtree: true });
    widget.dataset.hovEmptyStateObserved = 'true';
  };

  const initialize = (root = document) => {
    root.querySelectorAll(sectionSelector).forEach(initializeSection);
    refineProductReviewWidget(root);
    openRequestedReviewForm();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
